/**
 * PublicTrackingPage - Page de suivi publique (sans authentification)
 * Accessible via /public/suivi/:operationNumber
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Package, MapPin, CheckCircle, Clock, Search,
  RefreshCw, AlertTriangle, Navigation, Phone, ArrowRight, Mail
} from 'lucide-react';
import { getVehicleTypeLabel } from '../constants/vehicleTypes';
import { formatMerchandiseType } from '../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL;

// Fix Leaflet default icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Custom icons
const truckIcon = new L.DivIcon({
  className: 'custom-truck-marker',
  html: `<div style="
    background: #1db584;
    border-radius: 50%;
    width: 40px;
    height: 40px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 3px solid white;
    box-shadow: 0 2px 10px rgba(0,0,0,0.3);
    animation: pulse 2s infinite;
  ">
    <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
      <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18.5c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5zm13.5-9l1.96 2.5H17V9.5h2.5zm-1.5 9c-.83 0-1.5-.67-1.5-1.5s.67-1.5 1.5-1.5 1.5.67 1.5 1.5-.67 1.5-1.5 1.5z"/>
    </svg>
  </div>`,
  iconSize: [40, 40],
  iconAnchor: [20, 20],
  popupAnchor: [0, -20],
});

const startIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-green.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

const endIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Orange icon for intermediate stops
const stopIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-orange.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

// Auto-fit bounds component
const FitBounds = ({ bounds }) => {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length >= 2 && map) {
      const timeoutId = setTimeout(() => {
        try {
          // Check if map container still exists
          if (map && map.getContainer()) {
            map.fitBounds(bounds, { padding: [50, 50], maxZoom: 13 });
          }
        } catch (e) {
          // Map might be unmounted, ignore
        }
      }, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [map, bounds]);
  return null;
};

const PublicTrackingPage = () => {
  const { operationNumber } = useParams();
  const navigate = useNavigate();
  
  const [searchInput, setSearchInput] = useState(operationNumber || '');
  const [tracking, setTracking] = useState(null);
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [branding, setBranding] = useState({
    logo_url: null,
    company_name: 'Baobab Connect',
    primary_color: '#1db584'
  });

  // Charger le branding
  useEffect(() => {
    const fetchBranding = async () => {
      try {
        const response = await axios.get(`${API}/api/admin/branding`);
        if (response.data && response.data.company_name) {
          setBranding(prev => ({ ...prev, ...response.data }));
        }
      } catch (error) {
        // Utiliser les valeurs par défaut
      }
    };
    fetchBranding();
  }, []);

  // Helper pour les URLs d'images
  const getImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `${API}${url}`;
  };

  const fetchTracking = useCallback(async (opNumber) => {
    if (!opNumber || opNumber.length < 5) return;
    
    setLoading(true);
    setError(null);
    
    try {
      // Fetch tracking data (SEC-010 : transmet le token de lien signé `t`)
      const tokenParam = new URLSearchParams(window.location.search).get('t');
      const sig = tokenParam ? `?t=${encodeURIComponent(tokenParam)}` : '';
      const response = await axios.get(`${API}/api/tracking/public/${opNumber.toUpperCase()}${sig}`);
      setTracking(response.data);
      
      // Fetch route if has coordinates
      if (response.data.tracking?.departureCoordinates && response.data.tracking?.arrivalCoordinates) {
        try {
          const routeResponse = await axios.get(`${API}/api/tracking/route/public/${opNumber.toUpperCase()}${sig}`);
          setRoute(routeResponse.data);
        } catch (routeErr) {
          console.warn('Could not fetch route:', routeErr);
        }
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Opération non trouvée');
      setTracking(null);
      setRoute(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (operationNumber) {
      setSearchInput(operationNumber);
      fetchTracking(operationNumber);
    }
  }, [operationNumber, fetchTracking]);

  // Auto-refresh every 30 seconds if in transit
  useEffect(() => {
    if (!autoRefresh || !tracking || tracking.status !== 'charge_en_route') return;
    
    const interval = setInterval(() => {
      fetchTracking(tracking.operationNumber);
    }, 30000);
    
    return () => clearInterval(interval);
  }, [autoRefresh, tracking, fetchTracking]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      navigate(`/public/suivi/${searchInput.trim().toUpperCase()}`);
      fetchTracking(searchInput.trim());
    }
  };

  const getStepIcon = (step, isCompleted, isCurrent, isCancelled) => {
    if (isCancelled) return <AlertTriangle className="w-5 h-5 text-red-500" />;
    if (isCompleted) return <CheckCircle className="w-5 h-5 text-emerald-500" />;
    if (isCurrent) return <Clock className="w-5 h-5 text-blue-500 animate-pulse" />;
    return <div className="w-5 h-5 rounded-full border-2 border-gray-300" />;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Calculate map bounds including stops
  const getMapBounds = () => {
    if (!tracking?.tracking) return null;
    
    const bounds = [];
    const t = tracking.tracking;
    
    if (t.coordinates?.latitude && t.coordinates?.longitude) {
      bounds.push([t.coordinates.latitude, t.coordinates.longitude]);
    }
    if (t.departureCoordinates?.latitude && t.departureCoordinates?.longitude) {
      bounds.push([t.departureCoordinates.latitude, t.departureCoordinates.longitude]);
    }
    
    // Add stops coordinates
    if (tracking.stops && tracking.stops.length > 0) {
      tracking.stops.forEach(stop => {
        if (stop.coordinates?.lat && stop.coordinates?.lng) {
          bounds.push([stop.coordinates.lat, stop.coordinates.lng]);
        }
      });
    }
    
    if (t.arrivalCoordinates?.latitude && t.arrivalCoordinates?.longitude) {
      bounds.push([t.arrivalCoordinates.latitude, t.arrivalCoordinates.longitude]);
    }
    
    return bounds.length >= 2 ? bounds : null;
  };

  // Convert OSRM route to Leaflet format
  const getRoutePositions = () => {
    if (!route?.geometry?.coordinates) return null;
    return route.geometry.coordinates.map(coord => [coord[1], coord[0]]);
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              {branding.logo_url ? (
                <img 
                  src={getImageUrl(branding.logo_url)} 
                  alt={branding.company_name}
                  className="h-10 w-auto object-contain"
                />
              ) : (
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center shadow-md"
                  style={{ background: `linear-gradient(to bottom right, ${branding.primary_color}, #16a075)` }}
                >
                  <Package className="w-5 h-5 text-white" />
                </div>
              )}
              <div>
                <h1 className="text-lg font-bold text-gray-900">{branding.company_name}</h1>
                <p className="text-xs text-gray-500">Suivi de livraison</p>
              </div>
            </div>
            <a 
              href="/login" 
              className="text-sm text-emerald-600 hover:text-emerald-700 font-medium"
            >
              Se connecter
            </a>
          </div>
        </div>
      </header>

      <main className="flex-1 max-w-5xl mx-auto px-4 py-8 w-full">
        {/* Search Box */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <form onSubmit={handleSearch} className="flex gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Numéro d'opération, de transfert ou référence courte (ex: TL-K7X2-0220-01)"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value.toUpperCase())}
                  className="pl-10 h-12 text-lg"
                />
              </div>
              <Button 
                type="submit" 
                disabled={loading || !searchInput.trim()}
                className="h-12 px-6 bg-emerald-600 hover:bg-emerald-700"
              >
                {loading ? <RefreshCw className="w-5 h-5 animate-spin" /> : 'Suivre'}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Error State */}
        {error && (
          <Card className="mb-6 border-red-200 bg-red-50">
            <CardContent className="p-6 text-center">
              <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-red-800">{error}</h3>
              <p className="text-red-600 mt-1">
                Vérifiez le numéro d&apos;opération et réessayez.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Tracking Results */}
        {tracking && (
          <div className="space-y-6">
            {/* Status Header */}
            <Card>
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <Badge className={`mb-2 ${
                      tracking.cancelled ? 'bg-red-100 text-red-700' :
                      tracking.progress === 100 ? 'bg-emerald-100 text-emerald-700' :
                      'bg-blue-100 text-blue-700'
                    }`}>
                      {tracking.statusLabel}
                    </Badge>
                    <h2 className="text-2xl font-bold text-gray-900">
                      {tracking.operationNumber}
                    </h2>
                  </div>
                  {tracking.status === 'charge_en_route' && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => fetchTracking(tracking.operationNumber)}
                      disabled={loading}
                    >
                      <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                      Actualiser
                    </Button>
                  )}
                </div>

                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                    <span>Progression</span>
                    <span>{tracking.progress}%</span>
                  </div>
                  <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full rounded-full transition-all duration-500 ${
                        tracking.cancelled ? 'bg-red-500' : 'bg-emerald-500'
                      }`}
                      style={{ width: `${tracking.progress}%` }}
                    />
                  </div>
                </div>

                {/* Route Info */}
                <div className="p-4 bg-gray-50 rounded-lg">
                  {/* Départ → Étapes → Arrivée */}
                  <div className="flex flex-col gap-3">
                    {/* Départ */}
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-emerald-500 flex-shrink-0" />
                      <div>
                        <span className="text-xs font-medium text-emerald-600">Départ</span>
                        <p className="text-gray-900 font-semibold">{tracking.departure || 'Non défini'}</p>
                      </div>
                    </div>

                    {/* Étapes intermédiaires */}
                    {tracking.stops && tracking.stops.length > 0 && tracking.stops.map((stop, idx) => (
                      <div key={idx} className="flex items-center gap-3 ml-1">
                        <div className="w-2.5 h-2.5 rounded-full bg-orange-500 flex-shrink-0" />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <span className="text-xs font-medium text-orange-600">Étape {stop.order || idx + 1}</span>
                            {stop.status === 'completed' && (
                              <Badge className="text-[10px] bg-green-100 text-green-700 px-1.5 py-0">Terminée</Badge>
                            )}
                            {stop.status === 'in_progress' && (
                              <Badge className="text-[10px] bg-blue-100 text-blue-700 px-1.5 py-0">En cours</Badge>
                            )}
                          </div>
                          <p className="text-gray-700 text-sm">{stop.city || stop.address}</p>
                        </div>
                      </div>
                    ))}

                    {/* Arrivée */}
                    <div className="flex items-center gap-3">
                      <div className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0" />
                      <div>
                        <span className="text-xs font-medium text-red-600">Arrivée</span>
                        <p className="text-gray-900 font-semibold">{tracking.arrival || 'Non défini'}</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Route distance/duration */}
                {route?.distanceKm && (
                  <div className="flex items-center justify-center gap-6 mt-4 text-sm text-gray-500">
                    <span className="flex items-center gap-1">
                      <Navigation className="w-4 h-4" />
                      {route.distanceKm} km
                    </span>
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      ~{Math.round(route.durationMinutes / 60)}h{route.durationMinutes % 60 > 0 ? ` ${Math.round(route.durationMinutes % 60)}min` : ''}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Map */}
            {tracking.tracking && (
              <Card className="overflow-hidden">
                <div className="h-[400px]">
                  <MapContainer
                    center={[7.539989, -5.547080]}
                    zoom={7}
                    style={{ height: '100%', width: '100%' }}
                    scrollWheelZoom={true}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    
                    {getMapBounds() && <FitBounds bounds={getMapBounds()} />}
                    
                    {/* Route line - OSRM route or fallback */}
                    {route?.geometry?.coordinates && route.geometry.coordinates.length > 0 && (
                      <Polyline
                        key={`route-${tracking.operationNumber}`}
                        positions={route.geometry.coordinates.map(coord => [coord[1], coord[0]])}
                        pathOptions={{
                          color: '#1db584',
                          weight: 5,
                          opacity: 0.8
                        }}
                      />
                    )}
                    
                    {/* Fallback: straight line if no OSRM route */}
                    {!route?.geometry?.coordinates && tracking.tracking.departureCoordinates && tracking.tracking.arrivalCoordinates && (
                      <Polyline
                        positions={[
                          [tracking.tracking.departureCoordinates.latitude, tracking.tracking.departureCoordinates.longitude],
                          [tracking.tracking.arrivalCoordinates.latitude, tracking.tracking.arrivalCoordinates.longitude]
                        ]}
                        pathOptions={{
                          color: '#94a3b8',
                          weight: 3,
                          opacity: 0.6,
                          dashArray: '10, 10'
                        }}
                      />
                    )}
                    
                    {/* Departure marker */}
                    {tracking.tracking.departureCoordinates && (
                      <Marker 
                        position={[
                          tracking.tracking.departureCoordinates.latitude, 
                          tracking.tracking.departureCoordinates.longitude
                        ]}
                        icon={startIcon}
                      >
                        <Popup>
                          <strong className="text-emerald-600">Point de départ</strong>
                          <p>{tracking.departure}</p>
                        </Popup>
                      </Marker>
                    )}
                    
                    {/* Arrival marker */}
                    {tracking.tracking.arrivalCoordinates && (
                      <Marker 
                        position={[
                          tracking.tracking.arrivalCoordinates.latitude, 
                          tracking.tracking.arrivalCoordinates.longitude
                        ]}
                        icon={endIcon}
                      >
                        <Popup>
                          <strong className="text-red-600">Point d&apos;arrivée</strong>
                          <p>{tracking.arrival}</p>
                        </Popup>
                      </Marker>
                    )}
                    
                    {/* Intermediate stops markers (orange) */}
                    {tracking.stops && tracking.stops.length > 0 && tracking.stops.map((stop, idx) => (
                      stop.coordinates?.lat && stop.coordinates?.lng && (
                        <Marker
                          key={`stop-${idx}`}
                          position={[stop.coordinates.lat, stop.coordinates.lng]}
                          icon={stopIcon}
                        >
                          <Popup>
                            <div>
                              <strong className="text-orange-600">Étape {stop.order || idx + 1}</strong>
                              <p>{stop.city || stop.address}</p>
                              {stop.status === 'completed' && (
                                <span className="text-xs text-green-600">✓ Terminée</span>
                              )}
                              {stop.instructions && (
                                <p className="text-xs text-gray-500 mt-1 italic">"{stop.instructions}"</p>
                              )}
                            </div>
                          </Popup>
                        </Marker>
                      )
                    ))}
                    
                    {/* Truck marker (current position) */}
                    {tracking.tracking.coordinates && (
                      <Marker 
                        position={[
                          tracking.tracking.coordinates.latitude, 
                          tracking.tracking.coordinates.longitude
                        ]}
                        icon={truckIcon}
                      >
                        <Popup>
                          <div className="text-center">
                            <strong className="text-emerald-600">Position actuelle</strong>
                            {tracking.tracking.speed > 0 && (
                              <p className="text-sm text-gray-500">
                                Vitesse: {Math.round(tracking.tracking.speed)} km/h
                              </p>
                            )}
                          </div>
                        </Popup>
                      </Marker>
                    )}
                  </MapContainer>
                </div>
              </Card>
            )}

            {/* Timeline */}
            <Card>
              <CardContent className="p-6">
                <h3 className="font-semibold text-gray-900 mb-4">Suivi détaillé</h3>
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-[22px] top-0 bottom-0 w-0.5 bg-gray-200" />
                  
                  {/* Steps */}
                  <div className="space-y-6">
                    {tracking.timeline?.map((step, idx) => {
                      const isLastStep = idx === tracking.timeline.length - 1;
                      const isFullyCompleted = step.completed && isLastStep && tracking.progress === 100;
                      
                      return (
                        <div key={idx} className="relative flex items-start gap-4">
                          <div className={`relative z-10 w-11 h-11 rounded-full flex items-center justify-center ${
                            step.cancelled ? 'bg-red-100' :
                            step.completed ? 'bg-emerald-100' :
                            step.current ? 'bg-blue-100' :
                            'bg-white border-2 border-gray-200'
                          }`}>
                            {getStepIcon(step, step.completed, step.current, step.cancelled)}
                          </div>
                          <div className="flex-1 pt-2">
                            <p className={`font-medium ${
                              step.cancelled ? 'text-red-700' :
                              step.completed || step.current ? 'text-gray-900' :
                              'text-gray-400'
                            }`}>
                              {step.label}
                            </p>
                            {step.current && !step.cancelled && !isFullyCompleted && (
                              <p className="text-sm text-blue-600 mt-1">En cours...</p>
                            )}
                            {isFullyCompleted && (
                              <p className="text-sm text-emerald-600 mt-1">Termine</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Additional Info */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Package className="w-8 h-8 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Marchandise</p>
                      <p className="font-semibold text-sm">{formatMerchandiseType(tracking.merchandiseType) || 'Non spécifié'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center text-gray-500 font-bold text-xs">T</div>
                    <div>
                      <p className="text-xs text-gray-500">Poids</p>
                      <p className="font-semibold text-sm">{tracking.weight ? `${tracking.weight} tonnes` : 'Non specifie'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center">
                      <Navigation className="w-4 h-4 text-gray-500" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-500">Type camion</p>
                      <p className="font-semibold text-sm">{getVehicleTypeLabel(tracking.vehicleType) || 'Non spécifié'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <Clock className="w-8 h-8 text-gray-400" />
                    <div>
                      <p className="text-xs text-gray-500">Livraison</p>
                      <p className="font-semibold text-sm">
                        {tracking.deliveredAt 
                          ? `Livre`
                          : tracking.estimatedDelivery 
                            ? formatDate(tracking.estimatedDelivery)
                            : 'Non definie'
                        }
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Help */}
            <Card className="bg-gray-50 border-dashed">
              <CardContent className="p-4">
                <p className="text-sm text-gray-600 text-center mb-3">
                  Besoin d&apos;aide ? Contactez notre service client
                </p>
                <div className="flex flex-wrap items-center justify-center gap-4">
                  <a 
                    href="mailto:support@baobabco.net" 
                    className="inline-flex items-center gap-2 text-emerald-600 font-medium hover:text-emerald-700"
                  >
                    <Mail className="w-4 h-4" />
                    support@baobabco.net
                  </a>
                  <a 
                    href="tel:+2250500163737" 
                    className="inline-flex items-center gap-2 text-emerald-600 font-medium hover:text-emerald-700"
                  >
                    <Phone className="w-4 h-4" />
                    +225 05 00 16 37 37
                  </a>
                  <a 
                    href="tel:+2250564730000" 
                    className="inline-flex items-center gap-2 text-emerald-600 font-medium hover:text-emerald-700"
                  >
                    <Phone className="w-4 h-4" />
                    +225 05 64 73 00 00
                  </a>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Empty State - Instructions */}
        {!tracking && !error && !loading && (
          <Card>
            <CardContent className="p-8">
              <div className="text-center mb-8">
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Package className="w-8 h-8 text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  Suivi de votre livraison
                </h3>
                <p className="text-gray-500">
                  Consultez le statut de votre expedition en temps reel
                </p>
              </div>
              
              <div className="bg-gray-50 rounded-lg p-6 max-w-lg mx-auto">
                <h4 className="font-medium text-gray-900 mb-4">Comment suivre votre livraison</h4>
                <ol className="space-y-3 text-sm text-gray-600">
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xs font-medium">1</span>
                    <span>Retrouvez votre référence sur le bon de commande, le bon de livraison ou dans l'email de confirmation.</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xs font-medium">2</span>
                    <div>
                      <span>Saisissez l'une des références acceptées :</span>
                      <ul className="mt-2 space-y-1 pl-2 text-gray-500">
                        <li><span className="font-mono text-xs bg-gray-200 px-1 rounded">BAO-OP-20260115-0001-XXXX</span> — Opération spot</li>
                        <li><span className="font-mono text-xs bg-gray-200 px-1 rounded">TL-K7X2-0220-01</span> — Référence courte transfert</li>
                        <li><span className="font-mono text-xs bg-gray-200 px-1 rounded">BAO-OP-...-20260115-01</span> — Numéro complet transfert</li>
                      </ul>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="flex-shrink-0 w-6 h-6 bg-emerald-600 text-white rounded-full flex items-center justify-center text-xs font-medium">3</span>
                    <span>Cliquez sur <strong>Suivre</strong> pour afficher le statut en temps réel de votre livraison.</span>
                  </li>
                </ol>
              </div>
              
              <div className="text-center mt-6 text-sm text-gray-500">
                <p className="mb-2">Vous n&apos;avez pas de numéro d&apos;opération ? Contactez-nous :</p>
                <div className="flex flex-wrap items-center justify-center gap-4">
                  <a href="mailto:support@baobabco.net" className="text-emerald-600 hover:underline">
                    support@baobabco.net
                  </a>
                  <span className="text-gray-300">|</span>
                  <a href="tel:+2250500163737" className="text-emerald-600 hover:underline">
                    +225 05 00 16 37 37
                  </a>
                  <span className="text-gray-300">|</span>
                  <a href="tel:+2250564730000" className="text-emerald-600 hover:underline">
                    +225 05 64 73 00 00
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-white border-t mt-auto py-4">
        <div className="max-w-5xl mx-auto px-4 text-center text-sm text-gray-500">
          <p>© 2026 Baobab Connect - Tous droits reserves</p>
        </div>
      </footer>

      {/* CSS for pulse animation */}
      <style>{`
        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }
      `}</style>
    </div>
  );
};

export default PublicTrackingPage;
